import React from "react";
import DOMPurify from "dompurify";
import { Printer, X } from "lucide-react";

interface TicketPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  ticketHtml: string;
}

export const TicketPreviewModal: React.FC<TicketPreviewModalProps> = ({
  isOpen,
  onClose,
  ticketHtml,
}) => {
  const handlePrint = () => {
    // ESTRATEGIA DEFINITIVA PARA iOS: Abrir ventana nueva con blob URL
    
    // 1. Crear un blob con el HTML completo del ticket
    const fullHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Ticket - Pastelería Reyes</title>
        <style>
          /* Reseteo básico */
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body {
            font-family: 'Courier New', monospace;
            padding: 10px;
            background: white;
          }
          
          /* Estilos para impresión */
          @media print {
            body {
              margin: 0;
              padding: 10mm;
            }
            @page {
              size: 80mm auto;
              margin: 0;
            }
          }
          
          /* Botón de impresión automático (solo visible en pantalla) */
          #print-button {
            position: fixed;
            top: 10px;
            right: 10px;
            padding: 12px 24px;
            background: #2563eb;
            color: white;
            border: none;
            border-radius: 8px;
            font-size: 16px;
            font-weight: 600;
            cursor: pointer;
            box-shadow: 0 2px 8px rgba(0,0,0,0.15);
            z-index: 1000;
          }
          
          #print-button:hover {
            background: #1d4ed8;
          }
          
          @media print {
            #print-button {
              display: none;
            }
          }
        </style>
      </head>
      <body>
        <button id="print-button" onclick="window.print()">🖨️ Imprimir</button>
        ${ticketHtml.replace(/<\/?html>/gi, '').replace(/<\/?head>/gi, '').replace(/<\/?body>/gi, '')}
        <script>
          // Auto-imprimir cuando la página está lista (después de un pequeño delay para iOS)
          setTimeout(() => {
            window.print();
          }, 500);
        </script>
      </body>
      </html>
    `;

    // 2. Crear un Blob con el contenido HTML
    const blob = new Blob([fullHtml], { type: 'text/html' });
    const blobUrl = URL.createObjectURL(blob);
    
    // 3. Abrir en nueva ventana/pestaña
    const printWindow = window.open(blobUrl, '_blank');
    
    if (printWindow) {
      // Esperar a que la ventana se cargue
      printWindow.onload = () => {
        // Liberar el blob URL después de un tiempo
        setTimeout(() => {
          URL.revokeObjectURL(blobUrl);
        }, 1000);
      };
    } else {
      // Si el popup fue bloqueado, mostrar mensaje
      alert('Por favor, permite las ventanas emergentes para poder imprimir el ticket.');
      URL.revokeObjectURL(blobUrl);
    }
  };

  if (!isOpen) return null;

  // Extraer contenido del body para la preview
  const getBodyContent = (html: string): string => {
    const bodyMatch = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
    return bodyMatch ? bodyMatch[1] : html;
  };

  // Extraer estilos para la preview
  const getStyles = (html: string): string => {
    const styleMatch = html.match(/<style[^>]*>([\s\S]*?)<\/style>/gi);
    return styleMatch ? styleMatch.join("\n") : "";
  };

  const extractStyleText = (html: string): string => {
    const matches = [...html.matchAll(/<style[^>]*>([\s\S]*?)<\/style>/gi)];
    return matches.map((m) => m[1] ?? "").join("\n");
  };

  const sanitizedBodyHtml = DOMPurify.sanitize(getBodyContent(ticketHtml), {
    // Ticket HTML is generated by our app, but it contains DB-backed strings.
    // DOMPurify blocks event handlers / scripts by default; we still explicitly forbid risky tags.
    FORBID_TAGS: ["script", "iframe", "object", "embed", "link", "meta"],
    ALLOWED_TAGS: [
      "div",
      "span",
      "p",
      "strong",
      "b",
      "em",
      "i",
      "br",
      "hr",
      "table",
      "thead",
      "tbody",
      "tfoot",
      "tr",
      "td",
      "th",
      "ul",
      "ol",
      "li",
      "small",
      "h1",
      "h2",
      "h3",
    ],
    ALLOWED_ATTR: ["class", "style"],
  });

  // Render CSS as text content (no dangerouslySetInnerHTML) to prevent style-tag breakouts.
  const safeCssText = extractStyleText(ticketHtml).slice(0, 20000);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-background rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col border">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Printer className="w-5 h-5" />
            Vista Previa del Ticket
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-full transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content - Scrollable Preview */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="bg-muted p-4 rounded-lg border">
            <div dangerouslySetInnerHTML={{ __html: sanitizedBodyHtml }} />
            <style>{safeCssText}</style>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-4 border-t bg-muted/50">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 border border-border rounded-lg hover:bg-muted transition-colors font-medium"
          >
            Cerrar
          </button>
          <button
            onClick={handlePrint}
            className="flex-1 px-4 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium flex items-center justify-center gap-2"
          >
            <Printer className="w-5 h-5" />
            IMPRIMIR AHORA
          </button>
        </div>
      </div>
    </div>
  );
};
